import { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  Stack,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { get } from "src/utils/networkUtils";
import useWithUrlTenant from "src/hooks/useTenant";

export default function LeaderboardsPage() {
  const [loading, setLoading] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [stagePeriods, setStagePeriods] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState("");
  const [selectedStagePeriodId, setSelectedStagePeriodId] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const withTenant = useWithUrlTenant();

  useEffect(() => {
    get(withTenant(`/admin/divisions`)).then((response) => {
      setDivisions(response.divisions || []);
    });
  }, [withTenant]);

  useEffect(() => {
    setLoading(true);
    get(withTenant(`/admin/stage-periods?offset=0&limit=100`)).then(
      (response) => {
        setStagePeriods(response.stagePeriods || []);
        setLoading(false);
      }
    );
  }, [withTenant]);

  useEffect(() => {
    if (selectedDivisionId) {
      get(
        withTenant(
          `/admin/divisions/${selectedDivisionId}/leagues?filter=archived`
        )
      ).then((response) => {
        setLeagues(response.leagues || []);
      });
    } else {
      setLeagues([]);
    }
  }, [selectedDivisionId, withTenant]);

  const fetchLeaderboard = useCallback(
    (newOffset = 0) => {
      if (!selectedStagePeriodId) return;

      setLoading(true);
      const limit = 10;
      let url = `/admin/stagePeriodLeaderboard?stagePeriodId=${selectedStagePeriodId}&type=global&offset=${newOffset}&limit=${limit}`;
      if (selectedDivisionId) url += `&divisionId=${selectedDivisionId}`;

      get(url).then((response) => {
        const data = response.leaderboard || [];
        if (newOffset === 0) {
          setLeaderboard(data);
        } else {
          setLeaderboard((prev) => [...prev, ...data]);
        }
        setHasMore(data.length === limit);
        setLoading(false);
      });
    },
    [selectedStagePeriodId, selectedDivisionId]
  );

  useEffect(() => {
    setOffset(0);
    fetchLeaderboard(0);
  }, [selectedStagePeriodId, selectedDivisionId, fetchLeaderboard]);

  const lastRowRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          const newOffset = offset + 10;
          setOffset(newOffset);
          fetchLeaderboard(newOffset);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, offset, fetchLeaderboard]
  );

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
      >
        <Typography variant="h4" gutterBottom>
          Leaderboards
        </Typography>
      </Stack>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="division-select-label">Division</InputLabel>
        <Select
          labelId="division-select-label"
          id="division-select"
          value={selectedDivisionId}
          onChange={(e) => {
            setSelectedDivisionId(e.target.value);
            setSelectedStagePeriodId("");
            setSelectedLeagueId("");
            setOffset(0);
          }}
          IconComponent={KeyboardArrowDownIcon}
        >
          {divisions.map((division) => (
            <MenuItem key={division.divisionId} value={division.divisionId}>
              {division.divisionName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {leagues.length > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="league-select-label">League</InputLabel>
          <Select
            labelId="league-select-label"
            id="league-select"
            value={selectedLeagueId}
            onChange={(e) => {
              setSelectedLeagueId(e.target.value);
              setSelectedStagePeriodId("");
              setOffset(0);
            }}
            IconComponent={KeyboardArrowDownIcon}
          >
            {leagues.map((league) => (
              <MenuItem key={league.leagueId} value={league.leagueId}>
                {league.name || `League ${league.leagueId}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="stage-period-select-label">Stage Period</InputLabel>
        <Select
          labelId="stage-period-select-label"
          id="stage-period-select"
          value={selectedStagePeriodId}
          onChange={(e) => setSelectedStagePeriodId(e.target.value)}
          IconComponent={KeyboardArrowDownIcon}
        >
          {stagePeriods.map((period) => (
            <MenuItem key={period.stagePeriodId} value={period.stagePeriodId}>
              {period.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Card>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#464A53", textAlign: "center" }}>
                  Rank
                </TableCell>
                <TableCell sx={{ color: "#464A53", textAlign: "center" }}>
                  Username
                </TableCell>
                <TableCell sx={{ color: "#464A53", textAlign: "center" }}>
                  Score
                </TableCell>
                <TableCell sx={{ color: "#464A53", textAlign: "center" }}>
                  Winnings
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboard.map((row, index) => {
                const isLastRow = index === leaderboard.length - 1;
                return (
                  <TableRow
                    key={row.userId}
                    ref={isLastRow ? lastRowRef : null}
                  >
                    <TableCell
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#6A707E",
                        textAlign: "center",
                      }}
                    >
                      {row.rank}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#6A707E",
                        textAlign: "center",
                      }}
                    >
                      {row.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#6A707E",
                        textAlign: "center",
                      }}
                    >
                      {row.score}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#6A707E",
                        textAlign: "center",
                      }}
                    >
                      {row.draftWinnings}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </TableContainer>
        {!loading && leaderboard.length === 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1">No data available</Typography>
          </Box>
        )}
      </Card>
    </Container>
  );
}
